import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TextType } from '..';
import { SharedConstants, SharedCSSPropertyName, TextStyleTypes } from '@app/eds';

export type ButtonType =
  | 'primary'
  | 'primaryDisabled'
  | 'secondary'
  | 'secondaryDisabled'
  | 'textLink'
  | 'textLinkDisabled'
  | 'destructive'
  | 'destructiveDisabled'
  | 'destructiveTextLink'
  | 'destructiveTextLinkDisabled'
  | 'buttonLink'
  | 'buttonLinkDisabled'
  | 'buttonTextLink'
  | 'buttonTextLinkDisabled'
  | 'buttonTextLinkVertical'
  | 'buttonTextLinkVerticalDisabled'
@Component({
  selector: 'app-button[text][buttonClick]',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss']
})
export class ButtonComponent {
  // Text provides the button text.
  @Input() text: string = SharedConstants.EMPTY_STRING;

  // Height provide button height.
  @Input() height!: string;

  // width provide button width.
  @Input() width!: string;

  // Button styles like button border, background color, border radius.
  @Input() btnBorder!: string;
  @Input() bgColor!: string;
  @Input() borderRadius!: string;
  @Input() btnPadding!: string;

  // Type provide some predefined classes.
  @Input() buttonType!: ButtonType;

  // Icon styles like icon name, icon color, icon size.
  @Input() iconLeft: string = SharedConstants.EMPTY_STRING;
  @Input() iconRight: string = SharedConstants.EMPTY_STRING;
  @Input() iconColor: string = SharedConstants.EMPTY_STRING;
  @Input() iconSize: string = SharedConstants.EMPTY_STRING;

  // Icon class for extra css manipulation from parent component
  @Input() iconClass: string = SharedConstants.EMPTY_STRING;

  // Istextlinkbutton provides the parameter is link button or normal button.
  @Input() isTextLinkBtn = 'false';

  //  IsDisabled is used to check is button is disabled button
  @Input() isDisabled = false;

  // button text style
  @Input() textStyleClass: TextType = TextStyleTypes.BUTTON_REGULAR;

  // This prop will handle the click event.
  @Output() buttonClick = new EventEmitter();

  // Will emit when right icon is clicked.
  @Output() iconClickedRight = new EventEmitter();

  // Using this boolean to stop double emitting.
  isIconClicked = false;

  /**
   * This method is used to call the parent component.
   * Will not emit button click if icon is emitted to
   * prevent double emitting.
   */
  buttonOnClick = () => {
    !this.isIconClicked && this.buttonClick.emit();
    this.isIconClicked = false;
  };

  /**
   * Will emit when right icon is clicked,
   * NOTE: Since this fn is within the btn i.e
   * one event within another in that case 'event bubbling'
   * will take place so first this fn will run then
   * buttonOnClick fn will run.
   */
  handleRightIconClick = () => {
    this.isIconClicked = true;
    this.iconClickedRight.emit();
  };

  /**
   * Will emit when left icon is clicked,
   * NOTE: Since this fn is within the btn i.e
   * one event within another in that case 'event bubbling'
   * will take place so first this fn will run then
   * buttonOnClick fn will run.
   */
  handleLeftIconClick = () => {
    this.isIconClicked = true;
    this.iconClickedRight.emit();
  };

  /**
   * This method will help to return css of predefined types.
   *
   * @returns string
   */
  getClassName() {
    switch (this.buttonType) {
      case 'primaryDisabled':
        return 'primary disabled';
      case 'secondary':
        return 'secondary';
      case 'secondaryDisabled':
        return 'secondary disabled';
      case 'textLink':
        return 'textLink';
      case 'textLinkDisabled':
        return 'textLink disabled';
      case 'destructive':
        return 'destructive';
      case 'destructiveDisabled':
        return 'destructive disabled';
      case 'destructiveTextLink':
        return 'destructiveTextLink';
      case 'destructiveTextLinkDisabled':
        return 'destructiveTextLink disabled';
      case 'buttonLink':
        return 'buttonLink';
      case 'buttonLinkDisabled':
        return 'buttonLink disabled';
      case 'buttonTextLink':
        return 'buttonTextLink';
      case 'buttonTextLinkDisabled':
        return 'buttonTextLink disabled';
      case 'buttonTextLinkVertical':
        return 'buttonTextLinkVertical';
      case 'buttonTextLinkVerticalDisabled':
        return 'buttonTextLinkVertical disabled';
      default:
        return 'primary';
    }
  }

  /**
   * This method will help to return styles for button.
   *
   * @returns object
   */
  getButtonStyles() {
    const styleObject: { [key: string]: string } = {};

    this.height && (styleObject[SharedCSSPropertyName.HEIGHT] = this.height);

    this.width && (styleObject[SharedCSSPropertyName.WIDTH] = this.width);

    this.btnBorder && (styleObject[SharedCSSPropertyName.BORDER] = this.btnBorder);

    this.bgColor && (styleObject[SharedCSSPropertyName.BACKGROUND_COLOR] = this.bgColor);

    this.borderRadius &&
      (styleObject[SharedCSSPropertyName.BORDER_RADIUS] = this.borderRadius);

    this.btnPadding && (styleObject[SharedCSSPropertyName.PADDING] = this.btnPadding);

    return styleObject;
  }
}
